"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslations } from "next-intl";
import type { DefaultValues, UseFormReturn } from "react-hook-form";
import { useForm } from "react-hook-form";
import { z } from "zod";

// import { md, useMediaMinWidth } from "@/hooks/use-media-min-width";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItemWithoutDiv,
  UnstyledFormLabel,
} from "@/components/ui/form";

import {
  CurrencyInput,
  HeadingLabelCell,
  LabelCell,
  NumberDisplayCell,
  sum,
  Table,
  TableHeader,
  Tr,
} from "./shared";

const formSchema = z.object({
  discountingDiscountRate: z.number().positive(),
  discountingAdSpend: z.number().positive(),
  netSellingPrice: z.number().positive(),
  productCosts: z.number().positive(),
  freightCost: z.number().positive(),
  fulfillmentCost: z.number().positive(),
  transactionFees: z.number().positive(),
  adSpend: z.number().positive(),
  performanceMarketingBudget: z.number().positive(),

  //
  // fixedOperatingCosts: z.number().positive(),
});

const defaultValues = {
  discountingDiscountRate: 20,
  discountingAdSpend: 24,
  netSellingPrice: 100,
  productCosts: 14,
  freightCost: 8,
  fulfillmentCost: 10,
  transactionFees: 1,
  adSpend: 30,
  performanceMarketingBudget: 150_000,
  // fixedOperatingCosts: 100_000,
} satisfies DefaultValues<z.infer<typeof formSchema>>;

interface ContentProps {
  form: UseFormReturn<z.infer<typeof formSchema>>;
  discountingNetSellingPrice: number;
  discountingGrossProfit: number;
  discountingContributionMarginPerUnit: number;
  discountingContributionMarginPercent: number;
  unitsSold: number;
  discountingUnitsSold: number;
  grossProfit: number;
  contributionMarginPerUnit: number;
  contributionMarginPercent: number;
  variableCosts: number;
  t: ReturnType<typeof useTranslations<"Calculators">>;
  tCommon: ReturnType<typeof useTranslations<"Common">>;
}

export function DiscountingBusinessImpactCalculator() {
  const t = useTranslations("Calculators");
  const tCommon = useTranslations("Common");
  const form = useForm<z.infer<typeof formSchema>>({
    mode: "onChange",
    resolver: zodResolver(formSchema),
    defaultValues,
  });

  const variableCosts = sum(
    form.watch([
      "productCosts",
      "freightCost",
      "fulfillmentCost",
      "transactionFees",
    ])
  );

  const grossProfit = form.watch("netSellingPrice") - variableCosts;
  const discountingGrossProfit =
    form.watch("netSellingPrice") *
      ((100 - form.watch("discountingDiscountRate")) / 100) -
    variableCosts;
  const discountingNetSellingPrice =
    form.watch("netSellingPrice") *
    (1 - form.watch("discountingDiscountRate") / 100);
  const contributionMarginPerUnit = grossProfit - form.watch("adSpend");
  const contributionMarginPercent =
    (contributionMarginPerUnit / form.watch("netSellingPrice")) * 100;
  const discountingContributionMarginPerUnit =
    discountingGrossProfit - form.watch("discountingAdSpend");
  const discountingContributionMarginPercent =
    (discountingContributionMarginPerUnit / discountingNetSellingPrice) * 100;

  const unitsSold = Math.floor(
    form.watch("performanceMarketingBudget") / form.watch("adSpend")
  );

  const discountingUnitsSold = Math.floor(
    form.watch("performanceMarketingBudget") / form.watch("discountingAdSpend")
  );

  const tableProps = {
    form,
    discountingNetSellingPrice,
    discountingGrossProfit,
    discountingContributionMarginPerUnit,
    discountingContributionMarginPercent,
    unitsSold,
    discountingUnitsSold,
    grossProfit,
    contributionMarginPerUnit,
    contributionMarginPercent,
    variableCosts,
    t,
    tCommon,
  } satisfies ContentProps;

  return (
    <Form {...form}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
        }}
      >
        <TableHeader>
          <p className="text-body-md text-text-on-dark sticky top-0 font-semibold">
            {t("discountingCalculator")}
          </p>
        </TableHeader>
        <DesktopTable {...tableProps} />
        <MobileTable {...tableProps} />
      </form>
    </Form>
  );
}

function MobileTable({
  form,
  discountingNetSellingPrice,
  discountingGrossProfit,
  discountingContributionMarginPerUnit,
  discountingContributionMarginPercent,
  unitsSold,
  discountingUnitsSold,
  grossProfit,
  contributionMarginPerUnit,
  contributionMarginPercent,
  variableCosts,
  t,
  tCommon,
}: ContentProps) {
  return (
    <Table className="md:hidden">
      <colgroup>
        <col className="w-3/12" />
        <col className="w-3/12" />
        <col className="w-3/12" />
        <col className="w-3/12" />
      </colgroup>
      <tbody className="bg-surface">
        <Tr>
          <HeadingLabelCell colSpan={4}>{t("unitLevel")}</HeadingLabelCell>
        </Tr>
        <Tr>
          <HeadingLabelCell colSpan={2} className="text-right">
            {t("discounting")}
          </HeadingLabelCell>
          <HeadingLabelCell colSpan={2} className="text-right">
            {t("baseline")}
          </HeadingLabelCell>
        </Tr>

        <FormField
          control={form.control}
          name="discountingDiscountRate"
          render={({ field }) => (
            <FormItemWithoutDiv>
              <Tr>
                <LabelCell colSpan={4}>
                  <UnstyledFormLabel>{t("discountPercent")}</UnstyledFormLabel>
                </LabelCell>
              </Tr>
              <Tr>
                <td />
                <NumberDisplayCell>
                  <FormControl>
                    <CurrencyInput {...field} />
                  </FormControl>
                </NumberDisplayCell>
                <td />
                <td />
              </Tr>
            </FormItemWithoutDiv>
          )}
        />
        <FormField
          control={form.control}
          name="netSellingPrice"
          render={({ field }) => (
            <FormItemWithoutDiv>
              <Tr>
                <LabelCell colSpan={4}>
                  <UnstyledFormLabel>{t("netSellingPrice")}</UnstyledFormLabel>
                </LabelCell>
              </Tr>
              <Tr>
                <td />
                <NumberDisplayCell>
                  {discountingNetSellingPrice}
                </NumberDisplayCell>
                <td />
                <NumberDisplayCell>
                  <FormControl>
                    <CurrencyInput {...field} />
                  </FormControl>
                </NumberDisplayCell>
              </Tr>
            </FormItemWithoutDiv>
          )}
        />

        <Tr>
          <LabelCell colSpan={4} className="font-bold">
            {t("minusVariableCosts")}
          </LabelCell>
        </Tr>

        <FormField
          control={form.control}
          name="productCosts"
          render={({ field }) => (
            <FormItemWithoutDiv>
              <Tr>
                <LabelCell colSpan={4}>
                  <UnstyledFormLabel>{t("productCosts")}</UnstyledFormLabel>
                </LabelCell>
              </Tr>
              <Tr>
                <NumberDisplayCell>
                  {form.watch("productCosts")}
                </NumberDisplayCell>
                <td />
                <NumberDisplayCell>
                  <FormControl>
                    <CurrencyInput {...field} />
                  </FormControl>
                </NumberDisplayCell>
                <td />
              </Tr>
            </FormItemWithoutDiv>
          )}
        />

        <FormField
          control={form.control}
          name="freightCost"
          render={({ field }) => (
            <FormItemWithoutDiv>
              <Tr>
                <LabelCell colSpan={4}>
                  <UnstyledFormLabel>{t("freightCost")}</UnstyledFormLabel>
                </LabelCell>
              </Tr>
              <Tr>
                <NumberDisplayCell>
                  {form.watch("freightCost")}
                </NumberDisplayCell>
                <td />
                <NumberDisplayCell>
                  <FormControl>
                    <CurrencyInput {...field} />
                  </FormControl>
                </NumberDisplayCell>
                <td />
              </Tr>
            </FormItemWithoutDiv>
          )}
        />

        <FormField
          control={form.control}
          name="fulfillmentCost"
          render={({ field }) => (
            <FormItemWithoutDiv>
              <Tr>
                <LabelCell colSpan={4}>
                  <UnstyledFormLabel>{t("fulfillmentCost")}</UnstyledFormLabel>
                </LabelCell>
              </Tr>
              <Tr>
                <NumberDisplayCell>
                  {form.watch("fulfillmentCost")}
                </NumberDisplayCell>
                <td />
                <NumberDisplayCell>
                  <FormControl>
                    <CurrencyInput {...field} />
                  </FormControl>
                </NumberDisplayCell>
                <td />
              </Tr>
            </FormItemWithoutDiv>
          )}
        />

        <FormField
          control={form.control}
          name="transactionFees"
          render={({ field }) => (
            <FormItemWithoutDiv>
              <Tr>
                <LabelCell colSpan={4}>
                  <UnstyledFormLabel>{t("transactionFees")}</UnstyledFormLabel>
                </LabelCell>
              </Tr>
              <Tr className="border-b">
                <NumberDisplayCell>
                  {form.watch("transactionFees")}
                </NumberDisplayCell>
                <NumberDisplayCell>{variableCosts}</NumberDisplayCell>
                <NumberDisplayCell>
                  <FormControl>
                    <CurrencyInput {...field} />
                  </FormControl>
                </NumberDisplayCell>
                <NumberDisplayCell>{variableCosts}</NumberDisplayCell>
              </Tr>
            </FormItemWithoutDiv>
          )}
        />

        <Tr>
          <LabelCell colSpan={4} className="font-bold">
            {t("grossProfit")}
          </LabelCell>
        </Tr>
        <Tr>
          <td />
          <NumberDisplayCell className="font-bold">
            {discountingGrossProfit}
          </NumberDisplayCell>
          <td />
          <NumberDisplayCell className="font-bold">
            {grossProfit}
          </NumberDisplayCell>
        </Tr>

        <Tr>
          <LabelCell colSpan={4}>
            <UnstyledFormLabel>{t("adSpend")}</UnstyledFormLabel>
          </LabelCell>
        </Tr>

        <Tr className="border-b">
          <td />
          <NumberDisplayCell>
            <FormField
              control={form.control}
              name="discountingAdSpend"
              render={({ field }) => (
                <FormItemWithoutDiv>
                  <FormControl>
                    <CurrencyInput {...field} />
                  </FormControl>
                </FormItemWithoutDiv>
              )}
            />
          </NumberDisplayCell>
          <td />
          <NumberDisplayCell>
            <FormField
              control={form.control}
              name="adSpend"
              render={({ field }) => (
                <FormItemWithoutDiv>
                  <FormControl>
                    <CurrencyInput {...field} />
                  </FormControl>
                </FormItemWithoutDiv>
              )}
            />
          </NumberDisplayCell>
        </Tr>

        <Tr>
          <LabelCell colSpan={4} className="font-bold">
            {t("contributionMargin")}
          </LabelCell>
        </Tr>
        <Tr>
          <NumberDisplayCell className="font-bold">
            {discountingContributionMarginPerUnit}
          </NumberDisplayCell>
          <td />
          <NumberDisplayCell className="font-bold">
            {contributionMarginPerUnit}
          </NumberDisplayCell>
        </Tr>

        <Tr>
          <LabelCell colSpan={4} className="font-bold">
            {t("percent")}
          </LabelCell>
        </Tr>
        <Tr>
          <td />
          <NumberDisplayCell className="font-bold">
            {Math.round(discountingContributionMarginPercent)}%
          </NumberDisplayCell>
          <td />
          <NumberDisplayCell className="font-bold">
            {Math.round(contributionMarginPercent)}%
          </NumberDisplayCell>
        </Tr>

        <Tr>
          <LabelCell colSpan={4} />
        </Tr>

        <FormField
          control={form.control}
          name="performanceMarketingBudget"
          render={({ field }) => (
            <FormItemWithoutDiv>
              <Tr>
                <LabelCell colSpan={4}>
                  <UnstyledFormLabel>{t("totalAdSpend")}</UnstyledFormLabel>
                </LabelCell>
              </Tr>
              <Tr>
                <td />
                <NumberDisplayCell>
                  {form.watch("performanceMarketingBudget")}
                </NumberDisplayCell>
                <td />
                <NumberDisplayCell>
                  <FormControl>
                    <CurrencyInput {...field} />
                  </FormControl>
                </NumberDisplayCell>
              </Tr>
            </FormItemWithoutDiv>
          )}
        />
        <Tr>
          <LabelCell colSpan={4}>
            <UnstyledFormLabel>{t("unitsSold")}</UnstyledFormLabel>
          </LabelCell>
        </Tr>
        <Tr className="border-b">
          <td />
          <NumberDisplayCell>{discountingUnitsSold}</NumberDisplayCell>
          <td />
          <NumberDisplayCell>{unitsSold}</NumberDisplayCell>
        </Tr>

        <Tr>
          <LabelCell colSpan={4}>{t("revenue")}</LabelCell>
        </Tr>
        <Tr>
          <td />
          <NumberDisplayCell>
            {discountingUnitsSold * discountingNetSellingPrice}
          </NumberDisplayCell>
          <td />
          <NumberDisplayCell>
            {unitsSold * form.watch("netSellingPrice")}
          </NumberDisplayCell>
        </Tr>

        <Tr className="font-bold">
          <LabelCell colSpan={4}>{t("totalContributionMargin")}</LabelCell>
        </Tr>
        <Tr>
          <td />
          <NumberDisplayCell className="font-bold">
            {discountingUnitsSold * discountingContributionMarginPerUnit}
          </NumberDisplayCell>
          <td />
          <NumberDisplayCell className="font-bold">
            {unitsSold * contributionMarginPerUnit}
          </NumberDisplayCell>
        </Tr>
      </tbody>
      <tfoot className="py-4">
        <Tr className="border-x-0 border-b-0">
          <td colSpan={4} className="px-5 py-3 text-center">
            <button
              type="submit"
              className="hidden"
              onClick={(e) => e.preventDefault()}
            />
            <Button
              onClick={(e) => {
                e.preventDefault();
                Object.entries(defaultValues).forEach(([key, value]) => {
                  form.setValue(key as keyof typeof defaultValues, value);
                });
              }}
              variant="link"
              size="sm"
              className="text-body"
            >
              {tCommon("clearResults")}
            </Button>
          </td>
        </Tr>
      </tfoot>
    </Table>
  );
}

function DesktopTable({
  form,
  discountingNetSellingPrice,
  discountingGrossProfit,
  discountingContributionMarginPerUnit,
  discountingContributionMarginPercent,
  unitsSold,
  discountingUnitsSold,
  grossProfit,
  contributionMarginPerUnit,
  contributionMarginPercent,
  variableCosts,
  t,
  tCommon,
}: ContentProps) {
  return (
    <Table className="hidden md:table">
      <colgroup>
        <col className="w-4/12" />
        <col className="w-2/12" />
        <col className="w-2/12" />
        <col className="w-2/12" />
        <col className="w-2/12" />
      </colgroup>
      <tbody className="bg-surface">
        <Tr>
          <HeadingLabelCell />
          <LabelCell colSpan={2} className="text-right">
            {t("discounting")}
          </LabelCell>
          <LabelCell colSpan={2} className="text-right">
            {t("baseline")}
          </LabelCell>
        </Tr>

        <FormField
          control={form.control}
          name="discountingDiscountRate"
          render={({ field }) => (
            <FormItemWithoutDiv>
              <Tr>
                <LabelCell>
                  <UnstyledFormLabel>{t("discountPercent")}</UnstyledFormLabel>
                </LabelCell>
                <td />
                <NumberDisplayCell>
                  <FormControl>
                    <CurrencyInput {...field} />
                  </FormControl>
                </NumberDisplayCell>
                <td />
                <td />
              </Tr>
            </FormItemWithoutDiv>
          )}
        />
        <FormField
          control={form.control}
          name="netSellingPrice"
          render={({ field }) => (
            <FormItemWithoutDiv>
              <Tr>
                <LabelCell>
                  <UnstyledFormLabel>{t("netSellingPrice")}</UnstyledFormLabel>
                </LabelCell>
                <NumberDisplayCell />
                <NumberDisplayCell>
                  {discountingNetSellingPrice}
                </NumberDisplayCell>
                <NumberDisplayCell />
                <NumberDisplayCell>
                  <FormControl>
                    <CurrencyInput {...field} />
                  </FormControl>
                </NumberDisplayCell>
              </Tr>
            </FormItemWithoutDiv>
          )}
        />

        <Tr>
          <LabelCell className="italic" colSpan={4}>
            {t("minusVariableCosts")}
          </LabelCell>
        </Tr>

        <FormField
          control={form.control}
          name="productCosts"
          render={({ field }) => (
            <FormItemWithoutDiv>
              <Tr>
                <LabelCell>
                  <UnstyledFormLabel>{t("productCosts")}</UnstyledFormLabel>
                </LabelCell>
                <NumberDisplayCell>
                  {form.watch("productCosts")}
                </NumberDisplayCell>
                <NumberDisplayCell />
                <NumberDisplayCell>
                  <FormControl>
                    <CurrencyInput {...field} />
                  </FormControl>
                </NumberDisplayCell>
                <NumberDisplayCell />
              </Tr>
            </FormItemWithoutDiv>
          )}
        />

        <FormField
          control={form.control}
          name="freightCost"
          render={({ field }) => (
            <FormItemWithoutDiv>
              <Tr>
                <LabelCell>
                  <UnstyledFormLabel>{t("freightCost")}</UnstyledFormLabel>
                </LabelCell>
                <NumberDisplayCell>
                  {form.watch("freightCost")}
                </NumberDisplayCell>
                <NumberDisplayCell />
                <NumberDisplayCell>
                  <FormControl>
                    <CurrencyInput {...field} />
                  </FormControl>
                </NumberDisplayCell>
                <NumberDisplayCell />
              </Tr>
            </FormItemWithoutDiv>
          )}
        />

        <FormField
          control={form.control}
          name="fulfillmentCost"
          render={({ field }) => (
            <FormItemWithoutDiv>
              <Tr>
                <LabelCell>
                  <UnstyledFormLabel>{t("fulfillmentCost")}</UnstyledFormLabel>
                </LabelCell>
                <NumberDisplayCell>
                  {form.watch("fulfillmentCost")}
                </NumberDisplayCell>
                <NumberDisplayCell />
                <NumberDisplayCell>
                  <FormControl>
                    <CurrencyInput {...field} />
                  </FormControl>
                </NumberDisplayCell>
                <NumberDisplayCell />
              </Tr>
            </FormItemWithoutDiv>
          )}
        />

        <FormField
          control={form.control}
          name="transactionFees"
          render={({ field }) => (
            <FormItemWithoutDiv>
              <Tr className="border-b">
                <LabelCell>
                  <UnstyledFormLabel>{t("transactionFees")}</UnstyledFormLabel>
                </LabelCell>
                <NumberDisplayCell>
                  {form.watch("transactionFees")}
                </NumberDisplayCell>
                <NumberDisplayCell>{variableCosts}</NumberDisplayCell>
                <NumberDisplayCell>
                  <FormControl>
                    <CurrencyInput {...field} />
                  </FormControl>
                </NumberDisplayCell>
                <NumberDisplayCell>{variableCosts}</NumberDisplayCell>
              </Tr>
            </FormItemWithoutDiv>
          )}
        />

        <Tr>
          <LabelCell className="font-bold">{t("grossProfit")}</LabelCell>
          <NumberDisplayCell />
          <NumberDisplayCell className="font-bold">
            {discountingGrossProfit}
          </NumberDisplayCell>
          <NumberDisplayCell />
          <NumberDisplayCell className="font-bold">
            {grossProfit}
          </NumberDisplayCell>
        </Tr>

        <Tr className="border-b">
          <LabelCell>
            <UnstyledFormLabel>{t("adSpend")}</UnstyledFormLabel>
          </LabelCell>
          <NumberDisplayCell />
          <NumberDisplayCell>
            <FormField
              control={form.control}
              name="discountingAdSpend"
              render={({ field }) => (
                <FormItemWithoutDiv>
                  <FormControl>
                    <CurrencyInput {...field} />
                  </FormControl>
                </FormItemWithoutDiv>
              )}
            />
          </NumberDisplayCell>
          <NumberDisplayCell />

          <NumberDisplayCell>
            <FormField
              control={form.control}
              name="adSpend"
              render={({ field }) => (
                <FormItemWithoutDiv>
                  <FormControl>
                    <CurrencyInput {...field} />
                  </FormControl>
                </FormItemWithoutDiv>
              )}
            />
          </NumberDisplayCell>
        </Tr>

        <Tr>
          <LabelCell className="font-bold">{t("contributionMargin")}</LabelCell>
          <NumberDisplayCell />
          <NumberDisplayCell className="font-bold">
            {discountingContributionMarginPerUnit}
          </NumberDisplayCell>
          <NumberDisplayCell />
          <NumberDisplayCell className="font-bold">
            {contributionMarginPerUnit}
          </NumberDisplayCell>
        </Tr>

        <Tr>
          <LabelCell className="font-bold">{t("percent")}</LabelCell>
          <NumberDisplayCell />
          <NumberDisplayCell className="font-bold">
            {Math.round(discountingContributionMarginPercent)}%
          </NumberDisplayCell>
          <NumberDisplayCell />
          <NumberDisplayCell className="font-bold">
            {Math.round(contributionMarginPercent)}%
          </NumberDisplayCell>
        </Tr>

        <Tr>
          <LabelCell colSpan={5} />
        </Tr>

        <FormField
          control={form.control}
          name="performanceMarketingBudget"
          render={({ field }) => (
            <FormItemWithoutDiv>
              <Tr>
                <LabelCell>
                  <UnstyledFormLabel>{t("totalAdSpend")}</UnstyledFormLabel>
                </LabelCell>
                <NumberDisplayCell />
                <NumberDisplayCell>
                  {form.watch("performanceMarketingBudget")}
                </NumberDisplayCell>
                <NumberDisplayCell />
                <NumberDisplayCell>
                  <FormControl>
                    <CurrencyInput {...field} />
                  </FormControl>
                </NumberDisplayCell>
              </Tr>
            </FormItemWithoutDiv>
          )}
        />

        <Tr className="border-b">
          <LabelCell>
            <UnstyledFormLabel>{t("unitsSold")}</UnstyledFormLabel>
          </LabelCell>
          <NumberDisplayCell />
          <NumberDisplayCell>{discountingUnitsSold}</NumberDisplayCell>

          <NumberDisplayCell />
          <NumberDisplayCell>{unitsSold}</NumberDisplayCell>
        </Tr>

        <Tr>
          <LabelCell>{t("revenue")}</LabelCell>
          <NumberDisplayCell />
          <NumberDisplayCell>
            {discountingUnitsSold * discountingNetSellingPrice}
          </NumberDisplayCell>
          <NumberDisplayCell />
          <NumberDisplayCell>
            {unitsSold * form.watch("netSellingPrice")}
          </NumberDisplayCell>
        </Tr>
        <Tr className="border-b">
          <LabelCell className="font-bold" colSpan={2}>
            {t("totalContributionMargin")}
          </LabelCell>
          <NumberDisplayCell className="font-bold">
            {discountingUnitsSold * discountingContributionMarginPerUnit}
          </NumberDisplayCell>
          <NumberDisplayCell />
          <NumberDisplayCell className="font-bold">
            {unitsSold * contributionMarginPerUnit}
          </NumberDisplayCell>
        </Tr>
      </tbody>
      <tfoot className="py-4">
        <Tr className="border-x-0 border-b-0 md:border-x-0">
          <td colSpan={5} className="px-5 py-3 text-center">
            <button
              type="submit"
              className="hidden"
              onClick={(e) => e.preventDefault()}
            />
            <Button
              onClick={(e) => {
                e.preventDefault();
                Object.entries(defaultValues).forEach(([key, value]) => {
                  form.setValue(key as keyof typeof defaultValues, value);
                });
              }}
              variant="link"
              size="sm"
              className="text-body"
            >
              {tCommon("clearResults")}
            </Button>
          </td>
        </Tr>
      </tfoot>
    </Table>
  );
}
